'use client'
import { Edit, VisibilityOffOutlined, VisibilityOutlined, Delete } from '@mui/icons-material'
import { Box, IconButton, Skeleton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState, useEffect, memo } from 'react'
import { PRIMARY, WHITE } from '@/constants/colors'

type FormSectionTitleProps = {
  title: string
  onTitleChange?: (newTitle: string) => void
  onDelete?: () => void
  isDefaultSection?: boolean
  isSectionVisible?: boolean
  onToggleVisibility?: () => void
}

const FormSectionTitle = memo(
  ({ title, onTitleChange, onDelete, isDefaultSection, isSectionVisible = true, onToggleVisibility }: FormSectionTitleProps) => {
    const t = useTranslations('HOMEPAGE')
    const [isEditingTitle, setIsEditingTitle] = useState(false)
    const [editedTitle, setEditedTitle] = useState(title)

    useEffect(() => {
      setEditedTitle(title)
    }, [title])

    const handleTitleChange = () => {
      const trimmedTitle = editedTitle.trim()
      if (onTitleChange && trimmedTitle) {
        onTitleChange(trimmedTitle)
      } else {
        setEditedTitle(title)
      }
      setIsEditingTitle(false)
    }

    return (
      <Box sx={{ background: PRIMARY, p: 2, borderRadius: '8px', mb: 4 }}>
        {isEditingTitle ? (
          <Stack direction="row" spacing={1} sx={{
            alignItems: "center"
          }}>
            <TextField
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={handleTitleChange}
              onKeyDown={(e) => e.key === 'Enter' && handleTitleChange()}
              autoFocus
              size="medium"
              fullWidth
              variant="standard"
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    color: WHITE,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    '&:before, &:after': {
                      display: 'none',
                    },
                  },
                },
              }}
            />
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} sx={{
            alignItems: "center"
          }}>
            <Typography variant="h5" sx={{ color: WHITE, fontWeight: 700, flex: 1 }}>
              {title ? title : <Skeleton variant="rounded" width="50%" height={32} />}
            </Typography>
            <Tooltip title={t('EDIT_TITLE')}>
              <IconButton size="medium" onClick={() => setIsEditingTitle(true)} sx={{ color: WHITE }}>
                <Edit fontSize="medium" />
              </IconButton>
            </Tooltip>
            {isDefaultSection && onToggleVisibility && (
              <Tooltip title={isSectionVisible ? t('HIDE_SECTION') : t('SHOW_SECTION')}>
                <IconButton size="medium" onClick={onToggleVisibility} sx={{ color: WHITE }}>
                  {isSectionVisible ? <VisibilityOutlined fontSize="medium" /> : <VisibilityOffOutlined fontSize="medium" />}
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title={t('DELETE_SECTION')}>
                <IconButton size="medium" onClick={onDelete} sx={{ color: WHITE }}>
                  <Delete fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
    );
  },
)

FormSectionTitle.displayName = 'FormSectionTitle'

export default FormSectionTitle
